import { roundCents } from '@/utils/money'
import type { CalculatorContext } from '@/types/calculator'
import type { CarAffordabilityInputs, CarAffordabilityResult } from './types'

export function calculateCarAffordability(
  inputs: CarAffordabilityInputs,
  _context: CalculatorContext
): CarAffordabilityResult {
  const financeAmount = Math.max(0, inputs.vehiclePrice - inputs.deposit)
  const balloonAmount = financeAmount * inputs.balloonPercent
  const n = inputs.termYears * 12
  const r = inputs.interestRate / 12

  let monthlyInstalment = 0
  if (financeAmount > 0 && r > 0) {
    const balloonPv = balloonAmount / Math.pow(1 + r, n)
    monthlyInstalment = ((financeAmount - balloonPv) * r) / (1 - Math.pow(1 + r, -n))
  } else if (financeAmount > 0) {
    monthlyInstalment = (financeAmount - balloonAmount) / n
  }

  const totalFinanceCost = monthlyInstalment * n + balloonAmount - financeAmount
  const totalVehicleCost = financeAmount + totalFinanceCost + inputs.deposit
  const totalMonthlyOwnershipCost =
    monthlyInstalment + inputs.insuranceMonthly + inputs.fuelMonthly + inputs.maintenanceMonthly

  const affordabilityRatio =
    inputs.monthlyIncome > 0 ? totalMonthlyOwnershipCost / inputs.monthlyIncome : 0

  let affordable: CarAffordabilityResult['affordable'] = 'yes'
  if (affordabilityRatio > 0.35) {
    affordable = 'no'
  } else if (affordabilityRatio > 0.25) {
    affordable = 'maybe'
  }

  return {
    financeAmount: roundCents(financeAmount),
    monthlyInstalment: roundCents(monthlyInstalment),
    balloonAmount: roundCents(balloonAmount),
    totalFinanceCost: roundCents(totalFinanceCost),
    totalVehicleCost: roundCents(totalVehicleCost),
    totalMonthlyOwnershipCost: roundCents(totalMonthlyOwnershipCost),
    affordabilityRatio,
    affordable,
  }
}
